import * as React from 'react';

import { BillApi } from '@ClientApi/billApi';
import { SetupIntent } from '@Shared/interfaces/Stripe';
import { ICompanyModel } from '@Shared/models';

export const setupIntentRequiresMicrodeposits = (setupIntent?: Pick<SetupIntent, 'status' | 'next_action'>) => {
  return Boolean(setupIntent?.status === 'requires_action' && setupIntent?.next_action?.type === 'verify_with_microdeposits');
};

export const useStripeSubscriptionAndCheckout = (company: ICompanyModel, onError?: (error: any) => void) => {
  const [stripeCheckoutSession, setStripeCheckoutSession] = React.useState(null as any);
  const [activeStripeSubscription, setActiveStripeSubscription] = React.useState(null as any);
  const [processingPaymentInfo, setProcessingPaymentInfo] = React.useState(false);

  async function getSubscriptionAndCheckout() {
    try {
      const [res, setup] = await Promise.allSettled([BillApi.getSubscription(), BillApi.getInProgressSetupIntent()]);

      if ((res.status === 'fulfilled' && !res.value.subscription) || res.status === 'rejected') {
        const { subscription, checkoutSession } = await BillApi.createSubscription(company.billing_plan, true);
        setActiveStripeSubscription(subscription);
        setStripeCheckoutSession(checkoutSession);
      } else {
        setActiveStripeSubscription(res.value.subscription);

        if (setup.status === 'fulfilled' && setupIntentRequiresMicrodeposits(setup.value)) {
          setStripeCheckoutSession(setup.value);
        } else {
          const createCheckoutSessionRes = await BillApi.createSetupIntent(res.value.subscription.id);
          setStripeCheckoutSession(createCheckoutSessionRes.data);
        }
      }
    } catch (err) {
      onError?.(err);
    }
  }

  React.useEffect(() => {
    getSubscriptionAndCheckout();
  }, []);

  async function onUpdateStripePaymentMethod(paymentMethodId: string) {
    setStripeCheckoutSession(null);
    setProcessingPaymentInfo(true);

    try {
      return await BillApi.updatePaymentMethod(paymentMethodId, activeStripeSubscription.id);
    } catch (err) {
      onError?.(err);
    } finally {
      setProcessingPaymentInfo(false);
    }
  }

  async function cancelStripeCheckoutSession() {
    await BillApi.cancelSetupIntent(stripeCheckoutSession.id);
    setStripeCheckoutSession(null);
    setActiveStripeSubscription(null);
    await getSubscriptionAndCheckout();
  }

  return {
    stripeCheckoutSession,
    activeStripeSubscription,
    processingPaymentInfo,
    setStripeCheckoutSession,
    cancelStripeCheckoutSession,
    setActiveStripeSubscription,
    onUpdateStripePaymentMethod
  };
};
